import { computed, type Ref } from 'vue'

export interface NormalizedField {
  id: string
  key: string
  type: number
  options: string[]
  parentId: string | null
  triggerOperator: string | null
  triggerValue: string | null
  index: number
}

export interface ReportData {
  key: string
  value: any
  type: number
}

export function normalizeFields(rawFields: any[]): NormalizedField[] {
  if (!rawFields || rawFields.length === 0) return []

  return rawFields.map((f, idx) => ({
    id: String(f.id ?? f.temp_id),
    key: f.key,
    type: f.type,
    options: f.options || [],
    parentId: f.parent_field_id != null ? String(f.parent_field_id) : (f.parent_temp_id || null),
    triggerOperator: f.trigger_operator || null,
    triggerValue: f.trigger_value ?? null,
    index: f.index ?? idx,
  }))
}

export function evaluateTrigger(parentValue: any, operator: string, compareValue: string | null): boolean {
  if (operator === 'not_empty') {
    return parentValue !== '' && parentValue !== undefined && parentValue !== null
  }

  const val = compareValue ?? ''

  // Boolean comparison: convert both sides to string for reliable matching
  if (typeof parentValue === 'boolean') {
    const strParent = String(parentValue)
    switch (operator) {
      case '==': return strParent === val
      case '!=': return strParent !== val
      default: return false
    }
  }

  // Try numeric comparison
  const num = parseFloat(parentValue)
  const threshold = parseFloat(val)
  if (!isNaN(num) && !isNaN(threshold)) {
    switch (operator) {
      case '>=': return num >= threshold
      case '<=': return num <= threshold
      case '>': return num > threshold
      case '<': return num < threshold
      case '==': return num === threshold
      case '!=': return num !== threshold
    }
  }

  // Fall back to string comparison
  const strParent = String(parentValue ?? '')
  switch (operator) {
    case '==': return strParent === val
    case '!=': return strParent !== val
    default: return false
  }
}

export function useTemplateFields(rawFields: Ref<any[]>) {
  const normalized = computed(() => normalizeFields(rawFields.value))

  const sortedFields = computed(() => {
    return [...normalized.value].sort((a, b) => {
      if (a.index === null && b.index === null) return 0
      if (a.index === null) return 1
      if (b.index === null) return -1
      return a.index - b.index
    })
  })

  const fieldMetaMap = computed(() => {
    const map: Record<string, { parentId: string | null; triggerOperator: string | null; triggerValue: string | null }> = {}
    for (const field of normalized.value) {
      map[field.id] = { parentId: field.parentId, triggerOperator: field.triggerOperator, triggerValue: field.triggerValue }
    }
    return map
  })

  const isFieldVisible = (fieldId: string, values: Record<string, any>): boolean => {
    const meta = fieldMetaMap.value[fieldId]
    if (!meta || !meta.parentId) return true

    const parentValue = values[meta.parentId]
    if (!meta.triggerOperator || !evaluateTrigger(parentValue, meta.triggerOperator, meta.triggerValue)) return false

    return isFieldVisible(meta.parentId, values)
  }

  return {
    normalizedFields: normalized,
    sortedFields,
    fieldMetaMap,
    isFieldVisible,
  }
}
